import { ACCENT } from "@/config/theme";
import type { WeeklyTrend } from "@/features/cost/types/weekly-cost-report.types";
import { ConversionToK } from "./costCalculations.utils";
import {
  getLabelStyle,
  getPercentageLabel,
} from "./costChartFormatting.utils";

const TOOLTIP_STYLES = {
  container:
    "font-family:'Inter',system-ui,sans-serif;font-size:12px;background:var(--chart-tooltip-bg);border:1px solid var(--chart-tooltip-border);border-radius:10px;padding:8px;box-shadow:var(--shadow-chart-tooltip);min-width:180px;max-width:420px;box-sizing:border-box;white-space:normal;word-break:break-word;overflow-wrap:break-word;color:var(--chart-tooltip-text);",
  title:
    "font-weight:700;font-size:13px;color:var(--text-info);padding-bottom:8px;border-bottom:1px solid var(--chart-tooltip-border);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;",
  row: "padding:2px 0;",
  rowLabel: "color:var(--text-tertiary);font-size:11px;padding:2px 0;",
  rowValue:
    "color:var(--text-secondary);font-weight:600;text-align:right;padding:2px 0;",
  badge:
    "display:flex;align-items:center;justify-content:space-between;border-radius:6px;padding:5px 8px;",
  badgeLabel: "color:var(--text-secondary);font-size:11px;",
  badgeValue: "font-weight:700;font-size:12px;",
};

type UnifiedTooltipOptions =
  | TooltipVarianceOptions
  | TooltipWeeklyOptions
  | TooltipTrendOptions;

interface TooltipVarianceOptions {
  type: "variance";
  name: string;
  currentCost: number;
  previousCost: number;
  varianceAmount: number;
  variancePercentage: number;
}

interface TooltipWeeklyOptions {
  type: "weekly";
  title: string;
  currentCost: number;
  previousCost: number;
  currentLabel: string;
  previousLabel: string;
  changePercent?: number;
  chartType: "services" | "customer";
}

interface TooltipTrendOptions {
  type: "trend";
  data: WeeklyTrend;
  currentColor: string;
  previousColor: string;
  getPreviousWeekDate: (dateStr: string) => Date;
  formatDate: (date: Date) => string;
  isSpike: (cost: number) => boolean;
  avgDailyCost?: number;
}

/**
 * Build chart tooltip HTML string for supported tooltip types.
 * Dispatches to specialized builders depending on options.type
 * @param {UnifiedTooltipOptions} options
 * @returns {string} HTML string suitable for ECharts tooltip formatter
 */
export const buildTooltipForCharts = (options: UnifiedTooltipOptions): string => {
  switch (options.type) {
    case "variance":
      return buildVarianceTooltip(options);
    case "weekly":
      return buildWeeklyTooltip(options);
    case "trend":
      return buildTrendTooltip(options);
  }
};

/**
 * Build tooltip HTML for variance-type tooltips (previous/current/variance display).
 */
function buildVarianceTooltip(opts: TooltipVarianceOptions): string {
  const style = getLabelStyle(
    opts.previousCost,
    opts.currentCost,
    opts.variancePercentage,
  );
  const color =
    style === "pos"
      ? "var(--status-error)"
      : style === "neg"
        ? "var(--status-success)"
        : "var(--text-disabled)";
  const badgeBg =
    style === "pos"
      ? "var(--alert-error-bg)"
      : style === "neg"
        ? "var(--alert-success-bg)"
        : "var(--bg-muted-subtle)";
  const badgeBorder =
    style === "pos"
      ? "var(--border-error-soft)"
      : style === "neg"
        ? "var(--border-success-soft)"
        : "var(--border-muted-subtle)";
  const sign = opts.varianceAmount >= 0 ? "\u002B" : "\u2212";
  const pct = getPercentageLabel(
    opts.previousCost,
    opts.currentCost,
    opts.variancePercentage,
  );

  return `
    <div style="${TOOLTIP_STYLES.container}">
      <div style="${TOOLTIP_STYLES.title}">${opts.name}</div>
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="${TOOLTIP_STYLES.rowLabel}">Previous</td>
          <td style="${TOOLTIP_STYLES.rowValue}">$${ConversionToK(opts.previousCost)}</td>
        </tr>
        <tr>
          <td style="${TOOLTIP_STYLES.rowLabel}">Current</td>
          <td style="${TOOLTIP_STYLES.rowValue}">$${ConversionToK(opts.currentCost)}</td>
        </tr>
        <tr>
          <td colspan="2" style="padding-top:2px;">
            <div style="${TOOLTIP_STYLES.badge}background:${badgeBg};border:1px solid ${badgeBorder};">
              <span style="${TOOLTIP_STYLES.badgeLabel}">Variance</span>
              <span style="${TOOLTIP_STYLES.badgeValue}color:${color};">
                ${sign}$${ConversionToK(Math.abs(opts.varianceAmount))}
                ${pct !== "—" ? `<span style="opacity:0.85;margin-left:3px;">(${ConversionToK(Math.abs(opts.variancePercentage))})%</span>` : ""}
              </span>
            </div>
          </td>
        </tr>
      </table>
    </div>`;
}

/**
 * Build tooltip HTML for weekly-type tooltips, including optional WoW badge for customers.
 */
function buildWeeklyTooltip(opts: TooltipWeeklyOptions): string {
  const wow =
    opts.changePercent !== undefined
      ? opts.changePercent
      : opts.previousCost === 0
        ? null
        : ((opts.currentCost - opts.previousCost) / opts.previousCost) * 100;

  const wowHtml =
    wow !== null && opts.chartType === "customer"
      ? (() => {
          let color, bgCol, border;

          if (wow > 10) {
            color = `var(--status-error)`;
            bgCol = `var(--bg-error-soft)`;
            border = `var(--status-error)`;
          } else if (wow >= 5) {
            color = `var(--status-warning)`;
            bgCol = `var(--bg-warning-soft)`;
            border = `var(--status-warning)`;
          } else {
            color = `var(--status-success)`;
            bgCol = `var(--bg-success-soft)`;
            border = `var(--status-success)`;
          }

          return `
            <div style="margin-top:8px;">
              <span style="${TOOLTIP_STYLES.badge}background:${bgCol};border:1px solid ${border};">
                <span style="${TOOLTIP_STYLES.badgeLabel}">WoW</span>
                <span style="${TOOLTIP_STYLES.badgeValue}color:${color};">${wow.toFixed(1)}%</span>
              </span>
            </div>`;
        })()
      : "";

  return `
    <div style="font-size:12px; line-height:1.6; min-width:160px;">
      <div style="font-weight:700; color:${ACCENT.aqua}; margin-bottom:6px; font-size:13px;">
        ${opts.title}
      </div>
      <div style="color:var(--text-primary);">
        ${opts.currentLabel.trim()} : 
        <b style="color:var(--text-primary);">$${opts.currentCost.toFixed(1)}</b>
      </div>
      <div style="color:var(--text-primary);">
        ${opts.previousLabel.trim()} : 
        <b style="color:var(--text-primary);">$${opts.previousCost.toFixed(1)}</b>
      </div>
      ${wowHtml}
    </div>`;
}

/**
 * Build tooltip HTML for trend points comparing current and previous day entries.
 * Shows spike badge when value exceeds avgDailyCost threshold.
 */
function buildTrendTooltip(opts: TooltipTrendOptions): string {
  const currentDate = new Date(opts.data.usageDate);
  const previousDate = opts.getPreviousWeekDate(opts.data.usageDate);

  let spikeText = "";
  if (opts.isSpike(opts.data.currentCost)) {
    if (opts.avgDailyCost && opts.avgDailyCost > 0) {
      const pct = ((opts.data.currentCost - opts.avgDailyCost) / opts.avgDailyCost) * 100;
      const pctLabel = `${pct.toFixed(1)}%`;
      spikeText = `<div style="color:var(--status-error);font-weight:600;margin-top:4px">spike detected - ${pctLabel} more than avg daily cost</div>`;
    } else {
      spikeText = `<div style="color:var(--status-error);font-weight:600;margin-top:4px">Spike detected</div>`;
    }
  }

  return `
    <div style="font-size:11px">
      <div style="margin-bottom:4px">
        <span style="
          display:inline-block;
          width:7px;
          height:7px;
          border-radius:50%;
          background:${opts.currentColor};
          margin-right:5px">
        </span>
        ${opts.formatDate(currentDate)}:
        <b>$${ConversionToK(opts.data.currentCost)}</b>
      </div>

      <div style="margin-bottom:4px">
        <span style="
          display:inline-block;
          width:7px;
          height:7px;
          border-radius:50%;
          background:${opts.previousColor};
          margin-right:5px">
        </span>
        ${opts.formatDate(previousDate)}:
        <b>$${ConversionToK(opts.data.previousCost)}</b>
      </div>

      ${spikeText}
    </div>
  `;
}
